import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ChatSidebar from '../components/chat/ChatSidebar';
import ChatMessage from '../components/chat/ChatMessage';
import ChatInput from '../components/chat/ChatInput';
import EmptyChat from '../components/chat/EmptyChat';
import ChatSessionHeader from '../components/chat/ChatSessionHeader';
import ChatNavBar from '../components/chat/ChatNavBar';
import { BookSelectionModal } from '../components/chat/BookSelectionModal';
import { BookContextPills } from '../components/chat/BookContextPills';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import useGeneralChat from '../hooks/useGeneralChat';
import { useBookContext } from '../hooks/useBookContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { trackEvent } from '../utils/analytics';
import { Citation, EnhancedChatMessage } from '../types/educational-chat';

const GeneralChat: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { isMobile } = useIsMobile();
  const [isBookModalOpen, setIsBookModalOpen] = useState(false);
  const [attachments, setAttachments] = useState<Array<{
    type: 'image';
    data_url: string;
    file_name: string;
    mime_type: string;
  }>>([]);

  // Image expansion and modal state
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalMessage, setModalMessage] = useState<EnhancedChatMessage | null>(null);
  const [modalImageIndex, setModalImageIndex] = useState(0);

  // Book context management
  const { selectedBooks, removeBook, setBooks, getBookIds } = useBookContext();

  // Get initial session ID from URL (memoized to avoid re-creating on every render)
  const initialSessionId = useMemo(() => searchParams.get('session'), []);

  const {
    sessions,
    activeSessionId,
    messages,
    isLoading,
    isStreaming,
    streamingMessageId,
    agentStatus,
    error,
    sendMessage,
    createNewSession,
    selectSession,
    deleteSession,
    renameSession,
    stopStreaming
  } = useGeneralChat(initialSessionId);

  // NOTE: Session restoration from URL is now handled inside useGeneralChat
  // via initialSessionId parameter. This avoids race conditions and infinite loops.

  // Sync URL with activeSessionId for session persistence and "Return to Chat" support
  // This is safe now because selectSession is stable (no deps that cause infinite loops)
  useEffect(() => {
    if (activeSessionId) {
      const currentUrlSession = searchParams.get('session');
      if (currentUrlSession !== activeSessionId) {
        navigate(`/chat?session=${activeSessionId}`, { replace: true });
      }
    }
  }, [activeSessionId, navigate, searchParams]);

  // Close sidebar when switching to mobile view
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile]);

  // Auto-scroll to bottom when messages change
  // Use scrollTo() on container directly (more reliable than scrollIntoView on end marker)
  // Use 'auto' (instant) during streaming to prevent animation conflicts
  // Use 'smooth' after streaming completes for better UX
  useEffect(() => {
    if (messagesContainerRef.current && !isLoading) {
      const container = messagesContainerRef.current;
      container.scrollTo({
        top: container.scrollHeight,
        behavior: isStreaming ? 'auto' : 'smooth'
      });
    }
  }, [messages, isLoading, isStreaming]);

  // Inline guard hint shown when the user tries to send without a
  // book tagged. The general chat enforces "exactly one tagged book"
  // because retrieval needs a target — without it the bookreader has
  // no scope to search and the answer quality collapses.
  const [showTagBookHint, setShowTagBookHint] = useState(false);

  const handleSendMessage = async (text: string) => {
    if (getBookIds().length === 0) {
      setShowTagBookHint(true);
      setIsBookModalOpen(true);
      return;
    }
    setShowTagBookHint(false);
    const currentAttachments = attachments; // Capture current value
    setAttachments([]); // Clear immediately
    await sendMessage(text, getBookIds(), currentAttachments); // Send with captured value
  };

  const handleCitationClick = (citation: Citation) => {
    if (citation.book_id && citation.page_number) {
      // Store return info in sessionStorage
      sessionStorage.setItem('returnToChat', JSON.stringify({ sessionId: activeSessionId }));
      navigate(`/dhyana/${citation.book_id}?page=${citation.page_number}`);
    }
  };

  // Handle page link clicks from AI messages
  const handlePageLinkClick = (bookId: string, pageNumber: number) => {
    // Store return info in sessionStorage
    sessionStorage.setItem('returnToChat', JSON.stringify({ sessionId: activeSessionId }));
    navigate(`/dhyana/${bookId}?page=${pageNumber}`);
  };

  // Handle note link clicks from AI messages
  const handleNoteClick = (noteId: string) => {
    // Store return info in sessionStorage
    sessionStorage.setItem('returnToChat', JSON.stringify({ sessionId: activeSessionId }));
    // Navigate to simplified note route (noteId only, no bookId needed)
    navigate(`/note/${noteId}`);
  };

  const handleAddImages = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        setAttachments(prev => [...prev, {
          type: 'image',
          data_url: dataUrl,
          file_name: file.name,
          mime_type: file.type
        }]);
      };
      reader.readAsDataURL(file);
    });
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddBooks = () => {
    setIsBookModalOpen(true);
  };

  const toggleMessageExpansion = (messageId: string) => {
    setExpandedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const handleImageClick = (message: EnhancedChatMessage, imageIndex: number) => {
    setModalMessage(message);
    setModalImageIndex(imageIndex);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setModalMessage(null);
    setModalImageIndex(0);
  };

  const navigateModalImage = (direction: 'prev' | 'next') => {
    if (!modalMessage?.attachments) return;
    const totalImages = modalMessage.attachments.length;
    if (direction === 'prev') {
      setModalImageIndex((prev) => (prev - 1 + totalImages) % totalImages);
    } else {
      setModalImageIndex((prev) => (prev + 1) % totalImages);
    }
  };

  const hasMessages = messages.length > 0;
  const activeSession = sessions.find(s => s.id === activeSessionId);

  return (
    <div className={`chat-page-wrapper ${isDarkMode ? 'dark-theme' : 'light-theme'}`}>
      {/* Top Navigation Bar */}
      <ChatNavBar
        isSidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        isMobile={isMobile}
      />

      {/* Main Chat Layout */}
      <div className="chat-page-container">
        {/* Sidebar Overlay for Mobile */}
        {isMobile && sidebarOpen && (
          <div
            className="sidebar-overlay"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div className={`sidebar-wrapper ${sidebarOpen ? 'open' : 'closed'} ${isMobile ? 'mobile' : ''}`}>
          <ChatSidebar
            sessions={sessions}
            activeSessionId={activeSessionId}
            onNewChat={() => {
              createNewSession();
              // Clear session from URL when starting new chat
              navigate('/chat', { replace: true });
              if (isMobile) setSidebarOpen(false);
            }}
            onSelectSession={(id) => {
              selectSession(id);
              // Update URL to track current session for proper "Return to Chat" navigation
              navigate(`/chat?session=${id}`, { replace: true });
              if (isMobile) setSidebarOpen(false);
            }}
            onDeleteSession={deleteSession}
          />
        </div>

      {/* Main Chat Area */}
      <div className="chat-main-area">
        {/* Session Header - only when messages exist */}
        {activeSession && hasMessages && (
          <ChatSessionHeader
            sessionTitle={activeSession.title || 'New conversation'}
            onRename={(newTitle) => renameSession(activeSessionId!, newTitle)}
          />
        )}

        {/* Content Area */}
        <div className={`chat-content ${hasMessages ? 'with-messages' : 'empty'}`}>
          {!hasMessages ? (
            /* Empty State - Centered */
            <div className="empty-state-container">
              <EmptyChat
                userName={user?.name?.split(' ')[0] || 'there'}
              />
              {selectedBooks.length === 0 && attachments.length === 0 && (
                <p className="chat-guide-hint">
                  Get the most out of chat — see our{' '}
                  <a
                    href="/howtochat/"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent('chat_guide_click', { source: 'chat_empty_state' })}
                  >
                    quick guide
                  </a>{' '}→
                </p>
              )}
              <div className="centered-input-wrapper">
                {/* Book Context Pills — shows the "Tag a book" CTA when
                    empty so the user knows they need to pick one before
                    chatting. */}
                <BookContextPills
                  books={selectedBooks}
                  onRemove={removeBook}
                  onAddClick={handleAddBooks}
                />
                {showTagBookHint && selectedBooks.length === 0 && (
                  <div className="mb-2 px-1 text-xs text-amber-700 dark:text-amber-300">
                    Please tag a book first — Vidya needs to know which book to ground the answer in.
                  </div>
                )}

                <ChatInput
                  onSendMessage={handleSendMessage}
                  onStopStreaming={stopStreaming}
                  isLoading={isLoading}
                  isStreaming={isStreaming}
                  placeholder="Ask me anything..."
                  showAttachmentMenu={true}
                  onAddImages={handleAddImages}
                  onAddBooks={handleAddBooks}
                  attachments={attachments}
                  onRemoveAttachment={handleRemoveAttachment}
                />
              </div>
            </div>
          ) : (
            /* Messages View */
            <>
              <div ref={messagesContainerRef} className="messages-container">
                <div className="messages-list">
                  {messages.map((message, index) => (
                    <div key={message.id} className="message-wrapper">
                      <ChatMessage
                        message={message}
                        isStreaming={streamingMessageId === message.id}
                        onCitationClick={handleCitationClick}
                        useSparklesAvatar={true}
                        agentStatus={streamingMessageId === message.id ? agentStatus : undefined}
                        isExpanded={expandedMessages.has(message.id)}
                        onToggleExpand={() => toggleMessageExpansion(message.id)}
                        onImageClick={(imageIndex) => handleImageClick(message, imageIndex)}
                        onPageLinkClick={handlePageLinkClick}
                        onNoteClick={handleNoteClick}
                        isLastAiMessage={
                          message.message_type === 'ai'
                          && !messages.slice(index + 1).some(m => m.message_type === 'ai')
                        }
                        onExamCheatCodesClick={() => sendMessage('Help me with exam cheat codes for this topic 🎯', getBookIds())}
                      />
                    </div>
                  ))}


                  {error && (
                    <div className="error-message">
                      <p>{error}</p>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Input at Bottom */}
              <div className="bottom-input-container">
                <div className="input-wrapper">
                  {/* Book Context Pills — same "tag a book" guard as
                      empty state. After the first message lands the
                      tagged book usually stays sticky for the session,
                      but the CTA reappears if the user removes it. */}
                  <BookContextPills
                    books={selectedBooks}
                    onRemove={removeBook}
                    onAddClick={handleAddBooks}
                  />
                  {showTagBookHint && selectedBooks.length === 0 && (
                    <div className="mb-2 px-1 text-xs text-amber-700 dark:text-amber-300">
                      Please tag a book first — Vidya needs to know which book to ground the answer in.
                    </div>
                  )}

                  <ChatInput
                    onSendMessage={handleSendMessage}
                    onStopStreaming={stopStreaming}
                    isLoading={isLoading}
                    isStreaming={isStreaming}
                    placeholder="Ask me anything..."
                    showAttachmentMenu={true}
                    onAddImages={handleAddImages}
                    onAddBooks={handleAddBooks}
                    attachments={attachments}
                    onRemoveAttachment={handleRemoveAttachment}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Book Selection Modal */}
      <BookSelectionModal
        isOpen={isBookModalOpen}
        onClose={() => setIsBookModalOpen(false)}
        selectedBooks={selectedBooks}
        onApply={setBooks}
      />

      {/* Image Viewer Modal */}
      {showImageModal && modalMessage && modalMessage.attachments && modalMessage.attachments.length > 0 && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="image-modal-header">
              <div className="image-modal-title">
                <span className="image-counter">
                  {modalImageIndex + 1} / {modalMessage.attachments.length}
                </span>
              </div>
              <button onClick={closeImageModal} className="image-modal-close">
                ×
              </button>
            </div>

            <div className="image-modal-content">
              {modalMessage.attachments.length > 1 && (
                <button
                  onClick={() => navigateModalImage('prev')}
                  className="image-modal-nav image-modal-nav-prev"
                  aria-label="Previous image"
                >
                  ←
                </button>
              )}

              <div className="image-modal-image-container">
                <img
                  src={modalMessage.attachments[modalImageIndex].data_url || modalMessage.attachments[modalImageIndex].file_url}
                  alt={modalMessage.attachments[modalImageIndex].file_name || `Image ${modalImageIndex + 1}`}
                  className="image-modal-image"
                />
              </div>

              {modalMessage.attachments.length > 1 && (
                <button
                  onClick={() => navigateModalImage('next')}
                  className="image-modal-nav image-modal-nav-next"
                  aria-label="Next image"
                >
                  →
                </button>
              )}
            </div>

            {modalMessage.attachments.length > 1 && (
              <div className="image-modal-thumbnails">
                {modalMessage.attachments.map((attachment, index) => (
                  <div
                    key={index}
                    className={`image-modal-thumbnail ${index === modalImageIndex ? 'active' : ''}`}
                    onClick={() => setModalImageIndex(index)}
                  >
                    <img
                      src={attachment.data_url || attachment.file_url}
                      alt={`Thumbnail ${index + 1}`}
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="image-modal-footer">
              <p className="image-modal-message-text">{modalMessage.content_text}</p>
              {modalMessage.attachments[modalImageIndex].file_name && (
                <p className="image-modal-filename">
                  {modalMessage.attachments[modalImageIndex].file_name}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileChange}
          className="hidden"
          style={{ display: 'none' }}
        />
      </div>

      <style>{`
        /* Page Wrapper */
        .chat-page-wrapper {
          display: flex;
          flex-direction: column;
          height: 100vh;
          overflow: hidden;
        }

        /* Main Container - Dark theme (default) */
        .chat-page-container {
          display: flex;
          flex: 1;
          background-color: #030712;
          background-image: radial-gradient(circle, rgba(51, 65, 85, 0.3) 1.2px, transparent 1.2px);
          background-size: 24px 24px;
          overflow: hidden;
          position: relative;
        }

        /* Light theme overrides */
        .chat-page-wrapper.light-theme .chat-page-container {
          background-color: #FAFBFC;
          background-image: radial-gradient(circle, rgba(209, 213, 219, 0.5) 1.2px, transparent 1.2px);
        }

        /* Sidebar Overlay */
        .sidebar-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(4px);
          z-index: 998;
        }

        .light-theme .sidebar-overlay {
          background: rgba(0, 0, 0, 0.3);
        }

        /* Sidebar Wrapper */
        .sidebar-wrapper {
          position: relative;
          z-index: 999;
          transition: all 0.3s ease;
          flex-shrink: 0;
        }

        .sidebar-wrapper.closed:not(.mobile) {
          width: 0;
        }

        .sidebar-wrapper.open:not(.mobile) {
          width: auto;
        }

        .sidebar-wrapper.mobile {
          position: fixed;
          top: 0;
          left: 0;
          height: 100vh;
          transform: translateX(-100%);
          transition: transform 0.3s ease;
        }

        .sidebar-wrapper.mobile.open {
          transform: translateX(0);
        }

        /* Main Chat Area */
        .chat-main-area {
          flex: 1;
          display: flex;
          flex-direction: column;
          position: relative;
          z-index: 1;
          min-width: 0;
          background: transparent;
        }

        /* Chat Content */
        .chat-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .chat-content.empty {
          justify-content: center;
          align-items: center;
        }

        .chat-content.with-messages {
          justify-content: flex-start;
        }

        /* Empty State Container */
        .empty-state-container {
          width: 100%;
          max-width: 720px;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 0 24px;
        }

        .chat-guide-hint {
          text-align: center;
          font-size: 13px;
          color: #9CA3AF;
          margin: 8px 0 0;
        }
        .chat-guide-hint a {
          color: #06B6D4;
          text-decoration: none;
        }
        .chat-guide-hint a:hover {
          text-decoration: underline;
        }

        .centered-input-wrapper {
          width: 100%;
          margin-top: 48px;
        }

        /* Messages Container */
        .messages-container {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          /* Removed scroll-behavior: smooth - conflicts with JS scrollTo() */
        }

        .messages-list {
          max-width: 1100px;
          margin: 0 auto;
          padding: 24px 32px 280px;
          width: 100%;
        }

        .message-wrapper {
          margin-bottom: 24px;
        }

        .thinking-indicator {
          margin: 24px 0;
        }

        /* Bottom Input Container */
        .bottom-input-container {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(to top, 
            #030712 0%, 
            #030712 85%,
            rgba(3, 7, 18, 0.8) 95%,
            rgba(3, 7, 18, 0) 100%
          );
          padding: 24px 16px 32px;
          pointer-events: none;
        }

        .light-theme .bottom-input-container {
          background: linear-gradient(to top, 
            #FAFBFC 0%, 
            #FAFBFC 85%,
            rgba(250, 251, 252, 0.8) 95%,
            rgba(250, 251, 252, 0) 100%
          );
        }

        .bottom-input-container .input-wrapper {
          max-width: 1100px;
          margin: 0 auto;
          pointer-events: all;
        }

        /* Error Message */
        .error-message {
          max-width: 1060px;
          margin: 16px auto;
          padding: 16px;
          background: rgba(220, 38, 38, 0.1);
          border: 1px solid rgba(220, 38, 38, 0.3);
          border-radius: 12px;
          color: #FCA5A5;
          font-size: 14px;
        }

        .light-theme .error-message {
          background: rgba(220, 38, 38, 0.05);
          border: 1px solid rgba(220, 38, 38, 0.2);
          color: #DC2626;
        }

        /* Scrollbar Styling */
        .messages-container::-webkit-scrollbar {
          width: 8px;
        }

        .messages-container::-webkit-scrollbar-track {
          background: transparent;
        }

        .messages-container::-webkit-scrollbar-thumb {
          background: rgba(100, 116, 139, 0.3);
          border-radius: 999px;
        }

        .messages-container::-webkit-scrollbar-thumb:hover {
          background: rgba(100, 116, 139, 0.5);
        }

        .light-theme .messages-container::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.4);
        }

        .light-theme .messages-container::-webkit-scrollbar-thumb:hover {
          background: rgba(156, 163, 175, 0.6);
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .messages-list {
            padding: 24px 16px 120px;
          }

          .empty-state-container {
            padding: 0 16px;
          }

          .centered-input-wrapper {
            margin-top: 32px;
          }

          .bottom-input-container {
            padding: 16px 16px 24px;
          }
        }

        @media (min-width: 769px) {
          .sidebar-overlay {
            display: none;
          }
        }

        /* Image Modal Styles */
        .image-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(3, 7, 18, 0.9);
          backdrop-filter: blur(8px);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          animation: fadeIn 0.2s ease-out;
        }

        .light-theme .image-modal-overlay {
          background: rgba(0, 0, 0, 0.6);
        }

        .image-modal-container {
          background: rgba(15, 23, 42, 0.95);
          border: 1px solid rgba(100, 116, 139, 0.3);
          border-radius: 20px;
          max-width: 900px;
          width: 100%;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(6, 182, 212, 0.1);
          animation: slideUp 0.3s ease-out;
        }

        .light-theme .image-modal-container {
          background: rgba(255, 255, 255, 0.98);
          border: 1px solid rgba(209, 213, 219, 0.5);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2), 0 0 40px rgba(6, 182, 212, 0.05);
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .image-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid rgba(100, 116, 139, 0.2);
        }

        .light-theme .image-modal-header {
          border-bottom: 1px solid rgba(209, 213, 219, 0.5);
        }

        .image-modal-title {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .image-counter {
          font-size: 14px;
          color: #94A3B8;
          font-weight: 500;
        }

        .light-theme .image-counter {
          color: #6B7280;
        }

        .image-modal-close {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: rgba(100, 116, 139, 0.2);
          border: none;
          color: #E2E8F0;
          font-size: 24px;
          line-height: 1;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .light-theme .image-modal-close {
          background: rgba(107, 114, 128, 0.1);
          color: #6B7280;
        }

        .image-modal-close:hover {
          background: rgba(6, 182, 212, 0.2);
          color: #06B6D4;
          transform: scale(1.1);
        }

        .light-theme .image-modal-close:hover {
          background: rgba(6, 182, 212, 0.15);
          color: #0891B2;
        }

        .image-modal-content {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          padding: 20px;
          overflow: hidden;
        }

        .image-modal-image-container {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          max-height: 100%;
        }

        .image-modal-image {
          max-width: 100%;
          max-height: 60vh;
          object-fit: contain;
          border-radius: 12px;
        }

        .image-modal-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: rgba(3, 7, 18, 0.8);
          backdrop-filter: blur(4px);
          border: 1px solid rgba(100, 116, 139, 0.3);
          color: white;
          font-size: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          z-index: 10;
        }

        .light-theme .image-modal-nav {
          background: rgba(255, 255, 255, 0.9);
          border: 1px solid rgba(209, 213, 219, 0.5);
          color: #1F2937;
        }

        .image-modal-nav:hover {
          background: rgba(6, 182, 212, 0.3);
          border-color: rgba(6, 182, 212, 0.5);
          transform: translateY(-50%) scale(1.1);
        }

        .light-theme .image-modal-nav:hover {
          background: rgba(6, 182, 212, 0.15);
          border-color: rgba(6, 182, 212, 0.5);
          color: #0891B2;
        }

        .image-modal-nav-prev {
          left: 20px;
        }

        .image-modal-nav-next {
          right: 20px;
        }

        .image-modal-thumbnails {
          display: flex;
          gap: 8px;
          padding: 12px 20px;
          overflow-x: auto;
          border-top: 1px solid rgba(100, 116, 139, 0.2);
        }

        .light-theme .image-modal-thumbnails {
          border-top: 1px solid rgba(209, 213, 219, 0.5);
        }

        .image-modal-thumbnail {
          width: 60px;
          height: 60px;
          border-radius: 10px;
          overflow: hidden;
          border: 2px solid transparent;
          cursor: pointer;
          flex-shrink: 0;
          transition: all 0.2s;
        }

        .image-modal-thumbnail:hover {
          border-color: rgba(6, 182, 212, 0.5);
          transform: scale(1.05);
        }

        .image-modal-thumbnail.active {
          border-color: #06B6D4;
          box-shadow: 0 0 0 2px rgba(6, 182, 212, 0.2);
        }

        .image-modal-thumbnail img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .image-modal-footer {
          padding: 16px 20px;
          border-top: 1px solid rgba(100, 116, 139, 0.2);
          max-height: 120px;
          overflow-y: auto;
        }

        .light-theme .image-modal-footer {
          border-top: 1px solid rgba(209, 213, 219, 0.5);
        }

        .image-modal-message-text {
          font-size: 14px;
          color: #E2E8F0;
          margin: 0 0 8px 0;
          line-height: 1.5;
        }

        .light-theme .image-modal-message-text {
          color: #1F2937;
        }

        .image-modal-filename {
          font-size: 12px;
          color: #94A3B8;
          margin: 0;
        }

        .light-theme .image-modal-filename {
          color: #6B7280;
        }

        /* Modal Mobile Responsive */
        @media (max-width: 768px) {
          .image-modal-overlay {
            padding: 0;
          }

          .image-modal-container {
            border-radius: 0;
            max-height: 100vh;
            height: 100vh;
          }

          .image-modal-image {
            max-height: 50vh;
          }

          .image-modal-nav {
            width: 36px;
            height: 36px;
            font-size: 18px;
          }

          .image-modal-nav-prev {
            left: 10px;
          }

          .image-modal-nav-next {
            right: 10px;
          }

          .image-modal-thumbnail {
            width: 50px;
            height: 50px;
          }
        }
      `}</style>
    </div>
  );
};

export default GeneralChat;
